import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Menu } from 'lucide-react';
import ThemeToggle from './ThemeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TopBar({ onMenuClick }) {
  const navigate = useNavigate();
  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  const [q, setQ] = useState('');
  // Live 10Y Treasury pulled from treasury.gov via /api/rates/live.
  // `null` while loading so the ticker skeleton doesn't flash 4.112% and
  // then correct — the LO is trained to trust that number.
  const [us10y, setUs10y] = useState(null);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const v = q.trim();
    // Top-bar search lands on the dedicated /search results page —
    // intentionally different from the Leads table view so the LO knows
    // they've stepped into cross-pipeline territory.
    navigate(v ? `/search?q=${encodeURIComponent(v)}` : '/search');
  };

  // ⌘K / Ctrl+K focuses the search input from anywhere. (Desktop only —
  // the input is hidden on mobile where the dedicated /search page hero
  // is the primary entry point.)
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        document.querySelector('[data-testid="topbar-search-input"]')?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Live 10Y Treasury poll — refresh every 5 min. Treasury.gov only
  // publishes new closes once a day around 4:15 PM ET, so 5 minutes is
  // plenty to catch the post-close bump without hammering our own
  // backend cache. Backend caches for 10 min anyway.
  useEffect(() => {
    let cancelled = false;
    const fetchRates = async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/rates/live`);
        if (!cancelled) setUs10y(r.data?.us10y || null);
      } catch {
        // Silent fail — ticker just stays on its last value or skeletons.
      }
    };
    fetchRates();
    const id = setInterval(fetchRates, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <header className="sticky top-0 z-30 backdrop-blur-lg bg-white/85 dark:bg-neutral-950/80 border-b hairline">
      <div className="h-16 px-4 sm:px-6 lg:px-10 flex items-center gap-3 sm:gap-4">
        <button
          onClick={onMenuClick}
          aria-label="Open menu"
          data-testid="topbar-menu"
          className="lg:hidden h-10 w-10 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Live rates ticker chip — desktop XL only, hidden on tablets & phones */}
        <div className="hidden xl:flex items-center gap-4">
          <TickerItem
            label="10Y TSY"
            value={us10y ? `${us10y.yield_pct.toFixed(3)}%` : '—'}
            delta={
              us10y && typeof us10y.change_pct === 'number'
                ? `${us10y.change_pct >= 0 ? '+' : ''}${us10y.change_pct.toFixed(2)}`
                : null
            }
            title={
              us10y
                ? `Official U.S. Treasury daily close · ${us10y.as_of_date || ''}`
                : 'Loading 10-year Treasury…'
            }
            testId="ticker-10y-tsy"
          />
        </div>

        {/* Primary search — DESKTOP ONLY. On phones LOs use the dedicated      */}
        {/* /search page hero (already prominent + tap-friendly) instead of a   */}
        {/* cramped duplicate up here.                                          */}
        <form
          onSubmit={submitSearch}
          className="hidden md:flex flex-1 max-w-[640px] mx-auto items-center gap-2 h-11"
        >
          <div className="flex-1 relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500 pointer-events-none" />
            <input
              data-testid="topbar-search-input"
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search clients by name, phone, email, or property address…"
              className="w-full h-11 pl-11 pr-3 rounded-xl bg-neutral-50 dark:bg-neutral-900 border hairline text-[13.5px] font-semibold text-neutral-900 dark:text-neutral-100 placeholder:font-normal placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 dark:focus:border-[hsl(var(--kf-gold))] focus:bg-white dark:focus:bg-neutral-900 transition"
            />
          </div>
          <button
            type="submit"
            data-testid="topbar-search-submit"
            className="h-11 px-4 rounded-xl bg-neutral-900 dark:bg-[hsl(var(--kf-gold))] dark:text-neutral-900 text-white text-[12.5px] font-bold hover:bg-neutral-800 transition flex items-center gap-1.5"
          >
            <Search className="h-3.5 w-3.5 text-[hsl(var(--kf-gold))] dark:text-neutral-900" />
            Search
          </button>
        </form>

        {/* Mobile spacer — pushes profile pill to the right */}
        <div className="flex-1 md:hidden" />

        <div className="flex items-center gap-1">
          <ThemeToggle />

          <div className="ml-2 md:ml-3 md:pl-3 md:border-l hairline flex items-center gap-2">
            <div className="text-right leading-tight hidden lg:block">
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-semibold">{dateStr}</div>
              <div className="mono text-[11px] text-neutral-700 dark:text-neutral-300 font-semibold">
                {today.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function TickerItem({ label, value, delta, up, title, testId }) {
  const isUp = up || (delta && !delta.startsWith('-') && !delta.startsWith('0'));
  const isDown = delta && delta.startsWith('-');
  return (
    <div className="flex items-baseline gap-1.5" title={title} data-testid={testId}>
      <span className="text-[9.5px] uppercase tracking-[0.18em] text-neutral-400 font-bold">{label}</span>
      <span className="mono text-[12.5px] text-neutral-900 dark:text-neutral-100 font-semibold ticker">{value}</span>
      {delta && (
        <span className={`mono text-[10px] font-semibold ${isDown ? 'text-emerald-600' : isUp ? 'text-red-500' : 'text-neutral-400'}`}>
          {delta}
        </span>
      )}
    </div>
  );
}
